import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, type BriefingView, type CardView, type CrossFitPayload } from '../api';
import { reasonText } from '../reasons';
import { buildBoard, NO_TOPIC, pickTraining, type TopicGroup } from '../briefing';
import { StoryCard, StoryRow } from '../components/cards';
import { TrainingPanel, earlierCaptureDay } from '../components/crossfit';
import { BriefingEvents } from '../components/events/BriefingEvents';
import { CoverageLine, EditionStrip, QuietNotice, SinceCutoffNotice } from '../components/editions';
import { EditionContext, useOpenDetail } from '../components/detail-link';
import { editionLabel } from '../editions';
import { ErrorState, Loading, Notice, useAsync } from '../components/ui';
import { nextBooking, todayHeadline, todayState } from '../crossfit';
import { formatLocalDay, formatLocalDayShort, formatTime, localToday, plural } from '../format';

/** How many tail entries each "Weitere" step mounts. The rest stays unmounted until asked for. */
export const TAIL_STEP = 15;
export const TOPIC_PARAM = 'thema';

export function BriefingPage() {
  const { data, error, loading, reload } = useAsync(() => api.currentBriefing(), []);
  if (loading && !data) return <Loading label="Ausgabe wird geladen…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return null;
  return <BriefingBody view={data} />;
}

export function BriefingByIdPage() {
  const { id = '' } = useParams();
  const { data, error, loading, reload } = useAsync(() => api.briefing(id), [id]);
  if (loading && !data) return <Loading label="Ausgabe wird geladen…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return null;
  return <BriefingBody view={data} historical />;
}

export function BriefingBody({ view, historical = false, now = new Date() }: { view: BriefingView; historical?: boolean; now?: Date }) {
  const b = view.briefing;
  const board = useMemo(() => buildBoard(view), [view]);
  const [params, setParams] = useSearchParams();
  const selected = params.get(TOPIC_PARAM);

  if (!b) {
    return (
      <div className="state state-empty">
        <h1>Noch keine Ausgabe</h1>
        <p>{view.message === 'No edition has been published yet.' ? 'Es wurde noch keine Ausgabe veröffentlicht.' : (view.message ?? 'Es wurde noch keine Ausgabe veröffentlicht.')}</p>
        <p className="muted small">
          Ausgaben erscheinen täglich um 06:00, 09:00, 10:00, 12:00, 15:00, 18:00 und 20:00 (Europe/Zurich).
        </p>
      </div>
    );
  }

  const setTopic = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set(TOPIC_PARAM, id);
    else next.delete(TOPIC_PARAM);
    setParams(next, { replace: true });
  };

  const c = b.completeness ?? {};
  const edition = editionLabel(b.edition_type, view.edition?.label);
  const slotTime = view.edition?.at && !view.edition.legacy ? ` ${view.edition.at}` : '';
  const delta = Boolean(c.coverage_from) && Boolean(c.previous);
  const latest = view.navigation?.latest;
  const today = localToday(now);
  const olderThanToday = !historical && b.edition_date < today;
  const selectedGroup = selected ? board.groups.find((g) => g.id === selected) ?? null : null;

  return (
    <EditionContext.Provider value={b.id}>
      <header className="page-intro">
        <p className="eyebrow">
          {formatLocalDay(b.edition_date)} · {edition}
          {slotTime}
          {historical ? ' · Archiv' : ''}
        </p>
        <h1>{historical ? 'So stand es damals.' : c.quiet ? 'Ruhige Stunden.' : delta ? 'Was seither neu ist.' : 'Dein Tag auf einen Blick.'}</h1>
        <p className="page-lead">
          {historical
            ? 'Diese Ausgabe zeigt genau die Kartenversionen, die damals veröffentlicht wurden. Spätere Aktualisierungen erreichst du in den Details.'
            : delta
              ? 'Neue Meldungen und wesentliche Entwicklungen seit der letzten Ausgabe, nach Themen. Bekanntes ohne Neuigkeit wiederholt sich nicht.'
              : 'Nachrichten nach Themen. Dein Training daneben. Details dort, wo du sie brauchst.'}
        </p>
        <CoverageLine view={view} />
      </header>

      <EditionStrip view={view} />

      {historical && (
        <Notice kind="info" title="Historische Ausgabe">
          Unverändert seit der Veröffentlichung{b.published_at ? ` am ${formatLocalDayShort(b.edition_date)} um ${formatTime(b.published_at)}` : ''}.{' '}
          {latest && latest.id !== b.id ? (
            <Link to="/">
              Zur neuesten Ausgabe ({editionLabel(latest.edition_type, latest.label)}
              {latest.published_at ? `, ${formatTime(latest.published_at)}` : ''})
            </Link>
          ) : (
            <Link to="/">Zur aktuellen Ausgabe</Link>
          )}
        </Notice>
      )}
      {olderThanToday && (
        <Notice kind="warn" title="Keine Ausgabe von heute">
          Dies ist die {edition} vom {formatLocalDay(b.edition_date)}. Eine neuere Ausgabe liegt noch nicht vor.
        </Notice>
      )}

      <DayStrip view={view} board={board} now={now} />

      <QuietNotice view={view} />
      <SinceCutoffNotice view={view} />

      {c.partial && (
        <Notice kind="warn" title="Diese Ausgabe ist unvollständig">
          <ul>
            {(c.reasons ?? []).map((r, i) => (
              <li key={i}>{reasonText(r)}</li>
            ))}
          </ul>
        </Notice>
      )}
      {!c.partial && (c.reasons?.length ?? 0) > 0 && (
        <Notice kind="info" title="Hinweise zu dieser Ausgabe">
          <ul>
            {(c.reasons ?? []).map((r, i) => (
              <li key={i}>{reasonText(r)}</li>
            ))}
          </ul>
        </Notice>
      )}
      {c.inference_healthy === false && (
        <Notice kind="warn" title="Lokale Inferenz war nicht verfügbar">
          Karten ohne Zusammenfassung zeigen die Originaltexte der Quellen. Es wurde nichts erfunden.
        </Notice>
      )}

      <YourSubjects view={view} />

      <div className="board-layout">
        <section aria-labelledby="board-h" className="board-main">
          <div className="board-top">
            <h2 id="board-h">Deine Themen</h2>
            <TopicBar groups={board.groups} selected={selectedGroup?.id ?? null} onSelect={setTopic} />
          </div>
          {selected && !selectedGroup ? (
            <div className="state state-empty board-empty">
              <h3>In dieser Ausgabe gibt es nichts zu diesem Thema.</h3>
              <p className="muted">Andere Themen sind weiterhin da.</p>
              <button type="button" className="small-button" onClick={() => setTopic(null)}>
                Alle Themen anzeigen
              </button>
            </div>
          ) : board.groups.length === 0 && c.quiet ? null : board.groups.length === 0 ? (
            <div className="state state-empty board-empty">
              <h3>Keine Nachrichten in dieser Ausgabe.</h3>
              <p className="muted">
                Die Ausgabe enthält {plural(board.training.length, 'Trainingskarte', 'Trainingskarten')} und keine Meldungen. Ob Quellen
                fehlten, steht im <Link to="/datenstatus">Datenstatus</Link>.
              </p>
            </div>
          ) : selectedGroup ? (
            <div className="topic-board is-filtered">
              <TopicSection group={selectedGroup} single />
            </div>
          ) : (
            <AllTopics groups={board.groups} />
          )}
          {board.context.length > 0 && !selected && <ContextBlock cards={board.context} />}
          <p className="board-foot">
            Themen folgen den Quellen. Mehrere Berichte zum selben Ereignis bilden eine Meldung; unterschiedliche Entwicklungen
            bleiben getrennt. Die Reihenfolge ist die Rangfolge der Ausgabe.
          </p>
        </section>

        <aside className="board-aside" aria-label="Training und Hinweise">
          <TrainingPanel views={board.training} day={b.edition_date} />
          {board.events.length > 0 && <BriefingEvents cards={board.events} />}
          <AgendaPanel />
        </aside>
      </div>
    </EditionContext.Provider>
  );
}

/**
 * "Deine Subjekte": stories of this edition whose main actor is a subject the
 * reader tracked when the edition was built. Read from the entries' stored
 * scores, so an old edition shows what it showed then. Nothing is shown when
 * the reader tracks no subject in this edition.
 */
function YourSubjects({ view }: { view: BriefingView }) {
  const open = useOpenDetail();
  const all = [...view.sections.flatMap((s) => s.cards), ...view.more];
  const hits = all.filter((cv) => (cv.entry?.score?.subjects ?? []).length > 0);
  if (hits.length === 0) return null;
  return (
    <section className="subjects-block" aria-labelledby="subjects-h">
      <h2 id="subjects-h">Deine Subjekte</h2>
      <ul>
        {hits.map((cv) => (
          <li key={cv.version.id}>
            {(cv.entry?.score?.subjects ?? []).map((s, i) => (
              <span key={s.entity_id}>
                {i > 0 ? ', ' : ''}
                <Link to={`/subjekte/${s.entity_id}`}>{s.name}</Link>
                {s.via ? <span className="muted"> (über {s.via})</span> : null}
              </span>
            ))}
            {': '}
            <button type="button" className="text-button" onClick={(e) => open(cv.version.id, e.currentTarget)}>
              {cv.version.title}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DayStrip({ view, board, now }: { view: BriefingView; board: ReturnType<typeof buildBoard>; now: Date }) {
  const b = view.briefing!;
  const cf = pickTraining(board.training, b.edition_date).primary?.version.payload as CrossFitPayload | undefined;
  const state = cf ? todayState(cf) : null;
  const next = cf ? nextBooking(cf, { day: localToday(now), time: formatTime(now.toISOString()) }) : null;
  const topics = board.groups.filter((g) => g.id !== NO_TOPIC).length;
  return (
    <div className="day-strip" role="group" aria-label="Tagesübersicht">
      <div>
        <span className="day-icon" aria-hidden="true">
          ◈
        </span>
        <span>
          <strong>
            {plural(board.newsCount, 'Meldung', 'Meldungen')} · {plural(topics, 'Thema', 'Themen')} · {plural(board.sourceCount, 'Quelle', 'Quellen')}
          </strong>
          <small>
            {b.published_at ? `Veröffentlicht ${formatTime(b.published_at)}` : 'Nicht veröffentlicht'}
            {b.revision > 1 ? ` · Revision ${b.revision}` : ''}
            {b.completeness?.partial ? ' · unvollständig' : ''}
          </small>
        </span>
      </div>
      <div>
        <span className="day-icon" aria-hidden="true">
          ◇
        </span>
        <span>
          <strong>{state && cf ? (cf.local_date === localToday(now) ? todayHeadline(state) : `${formatLocalDayShort(cf.local_date)}: ${todayHeadline(state, false)}`) : 'Keine Trainingsdaten'}</strong>
          <small>
            {!cf
              ? 'Die Ausgabe enthält keine CrossFit-Karte'
              : cf.stale
                ? earlierCaptureDay(cf)
                  ? `Stand nicht aktuell: Aufnahme vom ${formatLocalDayShort(earlierCaptureDay(cf)!)}`
                  : 'Stand nicht aktuell'
                : cf.workout
                  ? 'Workout verfügbar'
                  : 'Kein Workout erfasst'}
          </small>
        </span>
      </div>
      <div>
        <span className="day-icon" aria-hidden="true">
          ↗
        </span>
        <span>
          <strong>{next ? `Nächster Kurs: ${next.local_date === localToday(now) ? 'heute' : formatLocalDayShort(next.local_date)}, ${next.time_start}` : 'Kein nächster Kurs bekannt'}</strong>
          <small>{next ? `${next.class_type}${next.own_status === 'waitlisted' ? ' · Warteliste' : ''}` : cf ? 'Keine spätere Anmeldung gelesen' : '–'}</small>
        </span>
      </div>
    </div>
  );
}

/**
 * Topic filters. Topics with front-page entries get buttons; topics that exist
 * only in the longer tail are offered in a compact select so the bar stays short.
 */
function TopicBar({ groups, selected, onSelect }: { groups: TopicGroup[]; selected: string | null; onSelect: (id: string | null) => void }) {
  const primary = groups.filter((g) => g.visible.length > 0);
  const others = groups.filter((g) => g.visible.length === 0);
  return (
    <div className="topic-bar" role="group" aria-label="Themen filtern">
      <button type="button" aria-pressed={selected === null} onClick={() => onSelect(null)}>
        Alle
      </button>
      {primary.map((g) => (
        <button key={g.id} type="button" aria-pressed={selected === g.id} onClick={() => onSelect(g.id)}>
          {g.label}
        </button>
      ))}
      {others.length > 0 && (
        <select
          aria-label="Weitere Themen"
          value={others.some((g) => g.id === selected) ? (selected as string) : ''}
          onChange={(e) => onSelect(e.target.value || null)}
        >
          <option value="">Weitere Themen …</option>
          {others.map((g) => (
            <option key={g.id} value={g.id}>
              {g.label} ({g.total})
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function AllTopics({ groups }: { groups: TopicGroup[] }) {
  const front = groups.filter((g) => g.visible.length > 0);
  const tailOnly = groups.filter((g) => g.visible.length === 0);
  return (
    <>
      <div className="topic-board">
        {front.map((g) => (
          <TopicSection key={g.id} group={g} />
        ))}
      </div>
      {tailOnly.length > 0 && (
        <section className="more-topics" aria-labelledby="more-topics-h">
          <h3 id="more-topics-h">Weitere Meldungen</h3>
          <p className="muted small">Themen, die es nicht auf die Titelseite geschafft haben – vollständig erreichbar.</p>
          <ul className="more-topic-list">
            {tailOnly.map((g) => (
              <li key={g.id}>
                <TopicSection group={g} collapsed />
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

function TopicSection({ group, single = false, collapsed = false }: { group: TopicGroup; single?: boolean; collapsed?: boolean }) {
  // Nothing of the tail is mounted until asked for; each step mounts TAIL_STEP more.
  const [shown, setShown] = useState(0);
  const [open, setOpen] = useState(!collapsed);
  const tailShown = group.tail.slice(0, shown);
  const remaining = group.tail.length - shown;
  const headingId = `topic-${group.id}`;
  const cls = ['topic-column', single ? 'is-single' : '', group.visible.length === 1 ? 'single-story' : '']
    .filter(Boolean)
    .join(' ');

  const count = (
    <span className="topic-count">
      {group.tail.length > 0
        ? `${group.visible.length} von ${plural(group.total, 'Meldung', 'Meldungen')}`
        : plural(group.total, 'Meldung', 'Meldungen')}
    </span>
  );

  if (collapsed) {
    return (
      <section className="topic-column is-collapsed" aria-labelledby={headingId}>
        <header className="topic-heading">
          <span className="topic-dot" aria-hidden="true" />
          <h3 id={headingId}>{group.label}</h3>
          <span className="topic-count">{plural(group.total, 'Meldung', 'Meldungen')}</span>
          <button
            type="button"
            className="small-button"
            aria-expanded={open}
            onClick={() => {
              setOpen(!open);
              if (!open && shown === 0) setShown(TAIL_STEP);
            }}
          >
            {open ? 'Einklappen' : 'Anzeigen'}
            <span className="visually-hidden"> {group.label}</span>
          </button>
        </header>
        {open && <TailList group={group} shown={shown} setShown={setShown} />}
      </section>
    );
  }

  return (
    <section className={cls} aria-labelledby={headingId} data-topic={group.id}>
      <header className="topic-heading">
        <span className="topic-dot" aria-hidden="true" />
        <h3 id={headingId}>{group.label}</h3>
        {count}
      </header>
      {group.visible.length > 0 && (
        <div className="topic-cards">
          {group.visible.map((cv) => (
            <StoryCard key={cv.version.id} view={cv} />
          ))}
        </div>
      )}
      {group.tail.length > 0 && (
        <div className="topic-tail">
          {tailShown.length > 0 && <TailRows items={tailShown} />}
          <div className="tail-controls">
            {remaining > 0 && (
              <button type="button" className="small-button" onClick={() => setShown(shown + TAIL_STEP)}>
                {shown === 0 ? `Weitere ${remaining} in ${group.label}` : `Weitere ${Math.min(TAIL_STEP, remaining)} von ${remaining} anzeigen`}
              </button>
            )}
            {shown > 0 && (
              <button type="button" className="text-button" onClick={() => setShown(0)}>
                Weniger anzeigen
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function TailList({ group, shown, setShown }: { group: TopicGroup; shown: number; setShown: (n: number) => void }) {
  const remaining = group.tail.length - shown;
  return (
    <div className="topic-tail">
      <TailRows items={group.tail.slice(0, shown)} />
      {remaining > 0 && (
        <div className="tail-controls">
          <button type="button" className="small-button" onClick={() => setShown(shown + TAIL_STEP)}>
            Weitere {Math.min(TAIL_STEP, remaining)} von {remaining} anzeigen
          </button>
        </div>
      )}
    </div>
  );
}

function TailRows({ items }: { items: CardView[] }) {
  return (
    <ul className="story-rows">
      {items.map((cv) => (
        <StoryRow key={cv.version.id} view={cv} />
      ))}
    </ul>
  );
}

/**
 * The first edition's background: important stories from yesterday, unchanged.
 * Compact rows under their own heading, so they never read as new.
 */
function ContextBlock({ cards }: { cards: CardView[] }) {
  return (
    <section className="context-block" aria-labelledby="context-h">
      <h3 id="context-h">Zum Einordnen</h3>
      <p className="muted small">Wichtige Meldungen von gestern, seither ohne neue Entwicklung. Sie wurden schon gezeigt.</p>
      <TailRows items={cards} />
    </section>
  );
}

function AgendaPanel() {
  const follows = useAsync(() => api.follows(false), []);
  const all = follows.data?.follows ?? null;
  const n = all ? all.filter((f) => f.subject_type !== 'entity').length : null;
  const nSubjects = all ? all.filter((f) => f.subject_type === 'entity').length : 0;
  return (
    <section className="panel" aria-labelledby="agenda-h">
      <h3 id="agenda-h">Weiter im Blick</h3>
      <div className="agenda-link">
        <strong>Verfolgt</strong>
        <span>
          {follows.error
            ? 'Konnte nicht geladen werden.'
            : n === null
              ? 'Wird geladen…'
              : n === 0 && nSubjects === 0
                ? 'Du verfolgst noch nichts.'
                : [n > 0 ? plural(n, 'Meldung', 'Meldungen') : null, nSubjects > 0 ? plural(nSubjects, 'Subjekt', 'Subjekte') : null]
                    .filter(Boolean)
                    .join(', ') + ' verfolgt.'}
        </span>
        <Link className="text-button" to="/verfolgt">
          Verfolgt ↗
        </Link>
      </div>
      <div className="agenda-link">
        <strong>Etwas wiederfinden?</strong>
        <span>Suche nach Quelle, Datum oder Begriff.</span>
        <Link className="text-button" to="/suche">
          Zur Suche ↗
        </Link>
      </div>
    </section>
  );
}
